import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: ReactNode;
  /** One element: the trigger. The bubble is placed against it. */
  children: ReactNode;
  side?: "top" | "bottom";
  delay?: number;
  /**
   * Keep the wrapper while there is no content, so the child isn't re-created, and loses no focus, when
   * content comes and goes.
   */
  keepWrapper?: boolean;
}

/**
 * Hover/focus tooltip, ported from the admin portal. It opens on hover, on the trigger's focus and on
 * tap, and closes on Escape or blur. The wrapper is `display: contents`, so it never changes layout.
 *
 * It is visual only: the trigger must carry the same text as its accessible description
 * (aria-describedby), because screen readers read the focused element, not this wrapper. `Button`'s
 * `requires` and `disabledReason` do that for you.
 */
export function Tooltip({ content, children, side = "top", delay = 250, keepWrapper }: TooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const timer = useRef(0);

  const show = useCallback((wait: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), wait);
  }, []);
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
    setPos(null);
  }, []);

  useLayoutEffect(() => {
    const trigger = ref.current?.firstElementChild;
    if (!open || !trigger || !tipRef.current) return;
    const r = trigger.getBoundingClientRect();
    const t = tipRef.current.getBoundingClientRect();
    let top = side === "top" ? r.top - t.height - 6 : r.bottom + 6;
    if (top < 4) top = r.bottom + 6;
    if (top + t.height > window.innerHeight - 4) top = r.top - t.height - 6;
    let left = r.left + r.width / 2 - t.width / 2;
    left = Math.max(4, Math.min(left, window.innerWidth - t.width - 4));
    setPos({ left, top });
  }, [open, side]);

  const none = content == null || content === "" || content === false;
  if (none && !keepWrapper) return <>{children}</>;

  return (
    // Pointer and focus events bubble here from the trigger, which is the interactive element.
    <span
      ref={ref}
      style={{ display: "contents" }}
      onMouseEnter={none ? undefined : () => show(delay)}
      onMouseLeave={none ? undefined : hide}
      onFocus={none ? undefined : () => show(0)}
      onBlur={none ? undefined : hide}
      onClick={none ? undefined : () => show(0)}
      onKeyDown={none ? undefined : (e) => e.key === "Escape" && hide()}
    >
      {children}
      {!none &&
        open &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            aria-hidden
            className="pointer-events-none fixed z-50 max-w-xs rounded-md bg-chrome-raised px-2 py-1 text-xs text-white shadow"
            style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
          >
            {content}
          </div>,
          document.body,
        )}
    </span>
  );
}
